import { axiosPrivate } from "@/lib/axios/axios-private";
import {
  clearAppTokens,
  clearClientAuthState,
  getAccessToken,
} from "@/lib/api/auth-token";
import { clearAuthProfile } from "@/lib/auth/profile";
import {
  clearSamlHandoffParams,
  getSamlCallbackUrl,
  getSamlLoginUrl,
  hasSamlHandoffParams,
} from "@/lib/auth/saml-config";
import {
  fetchAndStoreCurrentUser,
  hasStoredAccessToken,
  storeTokensFromHandoff,
} from "@/lib/auth/user-session";
import { navigateToRoleHome } from "@/lib/auth/rbac";
import {
  clearSignInPending,
  setSignInPhase,
} from "@/lib/auth/sign-in-pending";
import { ApiError } from "@/lib/api/errors";

type SamlLogoutResponse = {
  success?: boolean;
  data?: {
    samlLogoutUrl?: string;
  };
};

/** Start SP-initiated SAML login (browser leaves the SPA). */
export function loginWithSaml(): void {
  if (typeof window === "undefined") return;
  window.location.href = getSamlLoginUrl();
}

/**
 * Complete SAML handoff on `/auth/saml/callback`.
 * Backend ACS redirects here with `?accessToken=&refreshToken=` (application JWT pair).
 */
export async function completeSamlCallback(): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  const error = params.get("error")?.trim();
  if (error) {
    clearSamlHandoffParams();
    throw new ApiError(error, 401);
  }

  const accessToken = params.get("accessToken")?.trim();
  if (!accessToken) {
    return hasStoredAccessToken();
  }

  const refreshToken = params.get("refreshToken")?.trim() || undefined;

  setSignInPhase("backend");
  storeTokensFromHandoff(accessToken, refreshToken);
  clearSamlHandoffParams();
  return true;
}

export async function ensureSamlSession(): Promise<boolean> {
  if (hasSamlHandoffParams()) {
    return completeSamlCallback();
  }
  return hasStoredAccessToken();
}

export function navigateAfterSamlLogin(): void {
  if (typeof window === "undefined") return;
  window.location.replace(`${window.location.origin}/dashboard/`);
}

export async function finishSamlSignInAndNavigate(): Promise<void> {
  setSignInPhase("profile");
  const user = await fetchAndStoreCurrentUser();
  clearSignInPending();
  navigateToRoleHome(user);
}

/** Application logout + optional Azure SSO logout redirect. */
export async function logoutSaml(): Promise<void> {
  let samlLogoutUrl: string | undefined;

  try {
    const token = getAccessToken();
    if (token) {
      const { data } = await axiosPrivate.post<SamlLogoutResponse>(
        "/api/auth/saml/logout",
        {
          postLogoutRedirectUri:
            typeof window !== "undefined"
              ? `${window.location.origin}/`
              : undefined,
        },
      );
      samlLogoutUrl = data?.data?.samlLogoutUrl?.trim() || undefined;
    }
  } catch {
    // Continue with client cleanup even if logout API fails.
  }

  clearAppTokens();
  clearAuthProfile();
  clearClientAuthState();

  if (typeof window === "undefined") return;

  if (samlLogoutUrl) {
    window.location.replace(samlLogoutUrl);
    return;
  }

  window.location.replace(`${window.location.origin}/`);
}

export function getDefaultSamlPostLogoutUri(): string {
  return getSamlCallbackUrl().replace(/\/auth\/saml\/callback\/?$/, "/");
}
